import React, { useState, useEffect, useRef } from "react";
import "./CircuitBreakers.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "bootstrap-icons/font/bootstrap-icons.css";

import confirm from "../../../src/assets/svg/Icon (1).svg";
import wrong from "../../../src/assets/svg/Icon (2).svg";
import warning from "../../../src/assets/svg/Icon (3).svg";
import call from "../../../src/assets/svg/Icon (4).svg";
import { useSelector } from "react-redux";

import email from "../../../src/assets/svg/Frame 163050.svg";
import SMS from "../../../src/assets/svg/Frame 163050 (1).svg";
import Push from "../../../src/assets/svg/Frame 163050 (2).svg";
import Database from "../../../src/assets/svg/Frame 163050 (3).svg";

import historyIcon from "../../../src/assets/svg/Edit icon.svg";
import retryIcon from "../../../src/assets/svg/Edit icon (1).svg";
import editPencilIcon from "../../../src/assets/svg/Edit icon (2).svg";

const BASE_URL = "https://estbn.com/EstbnNew/public/api/portal";

const channelIconMap = {
  email: email,
  sms: SMS,
  push: Push,
  database: Database,
};

const getBreakerStatus = (breakerState) => {
  if (!breakerState) return "Unknown";
  if (breakerState.is_open) {
    return "Open";
  }
  if (
    breakerState.failure_count > 0 &&
    breakerState.failure_count >= breakerState.failure_threshold - 1 &&
    !breakerState.is_open
  ) {
    return "Warning";
  }
  return "Closed";
};

const statusClass = {
  Closed: "status-badge closed",
  Open: "status-badge open",
  Warning: "status-badge warning",
  Unknown: "status-badge unknown",
};

const CircuitBreakers = () => {
  const { user } = useSelector((state) => state.auth);

  const AUTH_TOKEN = user?.token;

  const [breakers, setBreakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toastMessage, setToastMessage] = useState({ type: "", text: "" });
  const toastRef = useRef(null);

  const createBreakerModalRef = useRef(null);
  const editBreakerModalRef = useRef(null);
  const retryConfirmationModalRef = useRef(null);
  const channelHistoryModalRef = useRef(null);

  const [newBreakerChannel, setNewBreakerChannel] = useState("");
  const [newBreakerFailureThreshold, setNewBreakerFailureThreshold] =
    useState(0);
  const [newBreakerResetTimeout, setNewBreakerResetTimeout] = useState(60);
  const [newBreakerIsActive, setNewBreakerIsActive] = useState(true);
  const [isSavingNewBreaker, setIsSavingNewBreaker] = useState(false);

  const [editingBreakerData, setEditingBreakerData] = useState(null);
  const [editBreakerChannel, setEditBreakerChannel] = useState("");
  const [editBreakerFailureThreshold, setEditBreakerFailureThreshold] =
    useState(0);
  const [editBreakerResetTimeout, setEditBreakerResetTimeout] = useState(60);
  const [editBreakerIsActive, setEditBreakerIsActive] = useState(true);
  const [isSavingEditedBreaker, setIsSavingEditedBreaker] = useState(false);

  const [selectedBreakerForHistory, setSelectedBreakerForHistory] =
    useState(null);
  const [channelHistory, setChannelHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [healthMetrics, setHealthMetrics] = useState({
    healthy_channels: 0,
    tripped_breakers: 0,
    channels_in_warning: 0,
    warning_threshold: 0,
    total_channels: 0,
    health_percentage: 0,
  });

  const [breakerToRetry, setBreakerToRetry] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const [chartData, setChartData] = useState([]);
  const [chartLines, setChartLines] = useState([]);

  const getBsModal = (ref) => {
    if (ref.current && window.bootstrap && window.bootstrap.Modal) {
      let instance = window.bootstrap.Modal.getInstance(ref.current);
      if (!instance) {
        instance = new window.bootstrap.Modal(ref.current);
      }
      return instance;
    }
    console.warn(
      "Bootstrap Modal not found or ref is null. Ensure bootstrap.bundle.min.js is loaded."
    );
    return null;
  };

  const handleShowCreateBreaker = () => {
    setNewBreakerChannel("");
    setNewBreakerFailureThreshold(0);
    setNewBreakerResetTimeout(60);
    setNewBreakerIsActive(true);
    setToastMessage({ type: "", text: "" });
    const bsModal = getBsModal(createBreakerModalRef);
    if (bsModal) bsModal.show();
  };

  const handleCloseCreateBreaker = () => {
    const bsModal = getBsModal(createBreakerModalRef);
    if (bsModal) bsModal.hide();
  };

  const handleEditAction = (breaker) => {
    setEditingBreakerData(breaker);
    const channelName =
      breaker.channel || breaker.state?.channel || breaker.state?.name || "";
    setEditBreakerChannel(channelName);
    setEditBreakerFailureThreshold(breaker.state?.failure_threshold || 0);
    setEditBreakerResetTimeout(breaker.state?.reset_timeout || 60);
    setEditBreakerIsActive(
      breaker.is_active !== undefined ? breaker.is_active : true
    );
    setToastMessage({ type: "", text: "" });
    const bsModal = getBsModal(editBreakerModalRef);
    if (bsModal) bsModal.show();
  };

  const handleCloseEditBreaker = () => {
    const bsModal = getBsModal(editBreakerModalRef);
    if (bsModal) bsModal.hide();
  };

  const handleShowChannelHistory = (breaker) => {
    const rawChannelName =
      breaker.channel || breaker.state?.channel || breaker.state?.name || "";
    const displayChannelName = rawChannelName
      ? rawChannelName.charAt(0).toUpperCase() + rawChannelName.slice(1)
      : "Loading...";

    setSelectedBreakerForHistory({
      ...breaker,
      displayChannelName: displayChannelName,
    });
    const bsModal = getBsModal(channelHistoryModalRef);
    if (bsModal) bsModal.show();
  };

  const handleCloseChannelHistory = () => {
    const bsModal = getBsModal(channelHistoryModalRef);
    if (bsModal) bsModal.hide();
    setSelectedBreakerForHistory(null);
    setChannelHistory([]);
    setHistoryError(null);
    setHistoryLoading(false);
  };

  const handleShowRetryConfirmation = (breaker) => {
    const rawChannelName =
      breaker.channel || breaker.state?.channel || breaker.state?.name || "";
    const displayChannelName = rawChannelName
      ? rawChannelName.charAt(0).toUpperCase() + rawChannelName.slice(1)
      : "Unknown Channel";

    setBreakerToRetry({ ...breaker, displayChannelName: displayChannelName });
    const bsModal = getBsModal(retryConfirmationModalRef);
    if (bsModal) bsModal.show();
  };

  const handleCloseRetryConfirmation = () => {
    const bsModal = getBsModal(retryConfirmationModalRef);
    if (bsModal) bsModal.hide();
    setBreakerToRetry(null);
    setIsRetrying(false);
  };

  const handleConfirmRetry = async () => {
    if (!breakerToRetry) {
      setToastMessage({
        type: "danger",
        text: "Error: No channel selected for retry.",
      });
      handleCloseRetryConfirmation();
      return;
    }

    setIsRetrying(true);
    setToastMessage({ type: "", text: "" });

    const channelNameForApi = (
      breakerToRetry.channel ||
      breakerToRetry.state?.channel ||
      breakerToRetry.state?.name
    ).toLowerCase();

    try {
      const response = await fetch(
        `${BASE_URL}/circuit-breakers/${channelNameForApi}/reset`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result.message ||
          `Failed to retry ${breakerToRetry.displayChannelName} channel.`;
        setToastMessage({ type: "danger", text: errorMessage });
        console.error("API Error (Retry):", result);
        return;
      }

      if (result.success) {
        setToastMessage({
          type: "success",
          text:
            result.message ||
            `${breakerToRetry.displayChannelName} channel connection retried successfully!`,
        });
        fetchCircuitBreakers();
        fetchHealthMetrics();
      } else {
        setToastMessage({
          type: "danger",
          text:
            result.message ||
            `Failed to retry ${breakerToRetry.displayChannelName} channel.`,
        });
      }
    } catch (err) {
      console.error("Network or unexpected error (Retry):", err);
      setToastMessage({
        type: "danger",
        text: `An unexpected error occurred during retry: ${err.message}`,
      });
    } finally {
      setIsRetrying(false);
      handleCloseRetryConfirmation();
    }
  };

  const fetchCircuitBreakers = async () => {
    try {
      const response = await fetch(`${BASE_URL}/circuit-breakers`, {
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }
      const result = await response.json();
      if (
        result.success &&
        result.data &&
        Array.isArray(result.data.breakers)
      ) {
        setBreakers(result.data.breakers);
      } else {
        setError(
          "API response for breakers did not contain expected data or was not successful."
        );
      }
    } catch (err) {
      console.error("Failed to fetch circuit breakers:", err);
      setError(
        err.message ||
          "An unknown error occurred while fetching circuit breakers."
      );
    }
  };

  const fetchHealthMetrics = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/circuit-breakers/health-metrics`,
        {
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }
      const result = await response.json();
      if (result.success && result.data) {
        setHealthMetrics(result.data);
      } else {
        setError(
          "API response for health metrics did not contain expected data or was not successful."
        );
      }
    } catch (err) {
      console.error("Failed to fetch health metrics:", err);
      setError(
        err.message ||
          "An unknown error occurred while fetching health metrics."
      );
    }
  };

  const fetchChannelHistory = async (channelName) => {
    if (!channelName) return;

    setHistoryLoading(true);
    setHistoryError(null);
    setChannelHistory([]);

    try {
      const response = await fetch(
        `${BASE_URL}/circuit-breakers/history/${channelName.toLowerCase()}`,
        {
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }
      const result = await response.json();
      if (result.success && result.data && Array.isArray(result.data.history)) {
        setChannelHistory(result.data.history);
      } else {
        setHistoryError(
          "API response for channel history did not contain expected data or was not successful."
        );
      }
    } catch (err) {
      console.error("Failed to fetch channel history:", err);
      setHistoryError(
        err.message || "An unknown error occurred while fetching history."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchCircuitBreakers(), fetchHealthMetrics()]);
      } catch (err) {
        console.error("Overall data fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedBreakerForHistory) {
      const channelName =
        selectedBreakerForHistory.channel ||
        selectedBreakerForHistory.state?.channel ||
        selectedBreakerForHistory.state?.name;
      if (channelName) {
        fetchChannelHistory(channelName);
      }
    }
  }, [selectedBreakerForHistory]);

  useEffect(() => {
    if (toastMessage.text && toastRef.current) {
      if (window.bootstrap && window.bootstrap.Toast) {
        const bsToast = new window.bootstrap.Toast(toastRef.current, {
          autohide: true,
          delay: 5000,
        });
        bsToast.show();
      } else {
        console.warn(
          "Bootstrap JS for Toasts not found. Ensure bootstrap.bundle.min.js is loaded."
        );
      }
    }
  }, [toastMessage]);

  useEffect(() => {
    const createModalElement = createBreakerModalRef.current;
    const editModalElement = editBreakerModalRef.current;
    const historyModalElement = channelHistoryModalRef.current;
    const retryModalElement = retryConfirmationModalRef.current;

    const onHiddenCreate = () => {};
    const onHiddenEdit = () => {
      setEditingBreakerData(null);
    };
    const onHiddenHistory = () => {
      setSelectedBreakerForHistory(null);
      setChannelHistory([]);
      setHistoryError(null);
      setHistoryLoading(false);
    };
    const onHiddenRetry = () => {
      setBreakerToRetry(null);
      setIsRetrying(false);
    };

    if (createModalElement)
      createModalElement.addEventListener("hidden.bs.modal", onHiddenCreate);
    if (editModalElement)
      editModalElement.addEventListener("hidden.bs.modal", onHiddenEdit);
    if (historyModalElement)
      historyModalElement.addEventListener("hidden.bs.modal", onHiddenHistory);
    if (retryModalElement)
      retryModalElement.addEventListener("hidden.bs.modal", onHiddenRetry);

    return () => {
      if (createModalElement)
        createModalElement.removeEventListener(
          "hidden.bs.modal",
          onHiddenCreate
        );
      if (editModalElement)
        editModalElement.removeEventListener("hidden.bs.modal", onHiddenEdit);
      if (historyModalElement)
        historyModalElement.removeEventListener(
          "hidden.bs.modal",
          onHiddenHistory
        );
      if (retryModalElement)
        retryModalElement.removeEventListener("hidden.bs.modal", onHiddenRetry);
    };
  }, []);

  useEffect(() => {
    if (breakers.length > 0) {
      const days = ["Mon", "Tue", "Wed", "Thu", "Fri"];
      const newChartData = [];
      const newChartLines = [];

      const colors = [
        "#8884d8",
        "#82ca9d",
        "#ffc658",
        "#a4de6c",
        "#d0ed57",
        "#fd7f6f",
        "#7eb0d5",
        "#b2e061",
        "#bd7ebe",
        "#ffb55a",
      ];

      const channels = Array.from(
        new Set(
          breakers.map(
            (b) =>
              b.channel?.toLowerCase() ||
              b.state?.channel?.toLowerCase() ||
              b.state?.name?.toLowerCase()
          )
        )
      ).filter(Boolean);

      const channelColors = {};
      channels.forEach((channel, index) => {
        channelColors[channel] = colors[index % colors.length];
        newChartLines.push({
          type: "monotone",
          dataKey: `${
            channel.charAt(0).toUpperCase() + channel.slice(1)
          } Failure`,
          stroke: channelColors[channel],
          strokeWidth: 2,
          dot: { r: 4 },
        });
      });

      days.forEach((day) => {
        const dayData = { name: day };
        channels.forEach((channel) => {
          dayData[
            `${channel.charAt(0).toUpperCase() + channel.slice(1)} Failure`
          ] = Math.floor(Math.random() * 5);
        });
        newChartData.push(dayData);
      });

      setChartData(newChartData);
      setChartLines(newChartLines);
    } else {
      setChartData([]);
      setChartLines([]);
    }
  }, [breakers]);

  const handleSaveNewBreaker = async () => {
    if (!newBreakerChannel || newBreakerChannel === "Select channel") {
      setToastMessage({ type: "danger", text: "Please select a channel." });
      return;
    }
    if (parseInt(newBreakerFailureThreshold, 10) < 1) {
      setToastMessage({
        type: "danger",
        text: "Failure Threshold must be at least 1.",
      });
      return;
    }
    if (parseInt(newBreakerResetTimeout, 10) < 0) {
      setToastMessage({
        type: "danger",
        text: "Reset Timeout cannot be negative.",
      });
      return;
    }

    setIsSavingNewBreaker(true);
    setToastMessage({ type: "", text: "" });

    try {
      const response = await fetch(`${BASE_URL}/circuit-breakers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          channel: newBreakerChannel.toLowerCase(),
          failure_threshold: parseInt(newBreakerFailureThreshold, 10),
          reset_timeout: parseInt(newBreakerResetTimeout, 10),
          is_active: newBreakerIsActive,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result.message || "Failed to create circuit breaker.";
        setToastMessage({ type: "danger", text: errorMessage });
        console.error("API Error (Create):", result);
        return;
      }

      if (result.success) {
        setToastMessage({
          type: "success",
          text: result.message || "Circuit breaker created successfully!",
        });
        fetchCircuitBreakers();
        fetchHealthMetrics();
        handleCloseCreateBreaker();
      } else {
        setToastMessage({
          type: "danger",
          text: result.message || "Failed to create circuit breaker.",
        });
      }
    } catch (err) {
      console.error("Network or unexpected error (Create):", err);
      setToastMessage({
        type: "danger",
        text: `An unexpected error occurred: ${err.message}`,
      });
    } finally {
      setIsSavingNewBreaker(false);
    }
  };

  const handleSaveEditedBreaker = async () => {
    if (!editingBreakerData || !editingBreakerData.channel) {
      setToastMessage({
        type: "danger",
        text: "Error: No circuit breaker selected for editing.",
      });
      return;
    }

    if (parseInt(editBreakerFailureThreshold, 10) < 1) {
      setToastMessage({
        type: "danger",
        text: "Failure Threshold must be at least 1.",
      });
      return;
    }
    if (parseInt(editBreakerResetTimeout, 10) < 0) {
      setToastMessage({
        type: "danger",
        text: "Reset Timeout cannot be negative.",
      });
      return;
    }

    setIsSavingEditedBreaker(true);
    setToastMessage({ type: "", text: "" });

    const channelToUpdate = (
      editingBreakerData.channel ||
      editingBreakerData.state?.channel ||
      editingBreakerData.state?.name
    ).toLowerCase();

    try {
      const response = await fetch(
        `${BASE_URL}/circuit-breakers/${channelToUpdate}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            channel: channelToUpdate,
            failure_threshold: parseInt(editBreakerFailureThreshold, 10),
            reset_timeout: parseInt(editBreakerResetTimeout, 10),
            is_active: editBreakerIsActive,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result.message || "Failed to update circuit breaker.";
        setToastMessage({ type: "danger", text: errorMessage });
        console.error("API Error (Update):", result);
        return;
      }

      if (result.success) {
        setToastMessage({
          type: "success",
          text: result.message || "Circuit breaker updated successfully!",
        });
        fetchCircuitBreakers();
        fetchHealthMetrics();
        handleCloseEditBreaker();
      } else {
        setToastMessage({
          type: "danger",
          text: result.message || "Failed to update circuit breaker.",
        });
      }
    } catch (err) {
      console.error("Network or unexpected error (Update):", err);
      setToastMessage({
        type: "danger",
        text: `An unexpected error occurred: ${err.message}`,
      });
    } finally {
      setIsSavingEditedBreaker(false);
    }
  };

  if (loading) {
    return (
      <div className="circuit-container px-4 py-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading circuit breaker data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="circuit-container px-4 py-4 text-center text-danger">
        <p>Error: {error}</p>
        <p>Please check the API endpoint, token, or network connection.</p>
      </div>
    );
  }

  return (
    <div className="circuit-container px-4 py-4">
      <div
        className="toast-container position-fixed top-0 end-0 p-3"
        style={{ zIndex: 1080 }}
      >
        {toastMessage.text && (
          <div
            className={`toast align-items-center text-white bg-${
              toastMessage.type === "success" ? "success" : "danger"
            } border-0`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            ref={toastRef}
          >
            <div className="d-flex">
              <div className="toast-body">{toastMessage.text}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                data-bs-dismiss="toast"
                aria-label="Close"
              ></button>
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-semibold mb-0">Circuit Breakers</h5>
        <button
          className="btn btn-outline-secondary rounded-3"
          onClick={handleShowCreateBreaker}
        >
          + Add New Breaker
        </button>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="summary-card shadow-sm text-center">
            <img src={confirm} alt="healthy" className="icon mb-2" />
            <div className="label">Healthy Channels</div>
            <div className="value">{healthMetrics.healthy_channels}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="summary-card shadow-sm text-center">
            <img src={wrong} alt="tripped" className="icon mb-2" />
            <div className="label">Tripped Breakers</div>
            <div className="value">{healthMetrics.tripped_breakers}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="summary-card shadow-sm text-center">
            <img src={warning} alt="warning" className="icon mb-2" />
            <div className="label">Warning Threshold</div>
            <div className="value">{healthMetrics.warning_threshold}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="summary-card shadow-sm text-center">
            <img src={call} alt="total" className="icon mb-2" />
            <div className="label">Total Channels</div>
            <div className="value">{healthMetrics.total_channels}</div>
          </div>
        </div>
      </div>

      <div className="table-wrapper bg-white rounded-4 shadow-sm mb-5">
        <table className="table align-middle mb-0">
          <thead>
            <tr className="table-heading">
              <th>Channels</th>
              <th>Status</th>
              <th>Failure Count</th>
              <th>Last Failure</th>
              <th>Threshold</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {breakers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4 text-muted">
                  No circuit breakers found.
                </td>
              </tr>
            ) : (
              breakers.map((breaker, idx) => {
                const actualChannelName =
                  breaker.channel ||
                  breaker.state?.channel ||
                  breaker.state?.name ||
                  "Unknown";
                const icon =
                  channelIconMap[actualChannelName.toLowerCase()] || SMS;

                const status = breaker.state
                  ? getBreakerStatus(breaker.state)
                  : "Unknown";
                const failureCount = breaker.state
                  ? `${breaker.state.failure_count}/${breaker.state.failure_threshold}`
                  : "N/A";
                const lastFailureText = breaker.state?.last_failure_at
                  ? new Date(breaker.state.last_failure_at).toLocaleString()
                  : "Never";
                const thresholdText = breaker.state?.failure_threshold
                  ? `${breaker.state.failure_threshold} failures`
                  : "N/A";

                return (
                  <tr key={idx}>
                    <td>
                      <div className="d-inline-flex align-items-center">
                        <img
                          src={icon}
                          alt={actualChannelName}
                          className="me-2"
                          style={{ width: 24, height: 24 }}
                        />
                        {actualChannelName}
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          breaker.state
                            ? statusClass[status]
                            : statusClass["Unknown"]
                        }
                      >
                        {status}
                      </span>
                    </td>
                    <td>{failureCount}</td>
                    <td>{lastFailureText}</td>
                    <td>{thresholdText}</td>
                    <td>
                      <div className="d-inline-flex align-items-center action-icons-group">
                        <img
                          src={historyIcon}
                          alt="History"
                          className="action-icon"
                          onClick={() => handleShowChannelHistory(breaker)}
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          src={retryIcon}
                          alt="Retry"
                          className="action-icon"
                          onClick={() => handleShowRetryConfirmation(breaker)}
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          src={editPencilIcon}
                          alt="Edit"
                          className="action-icon"
                          onClick={() => handleEditAction(breaker)}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="chart-wrapper bg-white rounded-4 shadow-sm p-4">
        <h6 className="mb-4">Failure Trends</h6>
        {chartData.length > 0 && chartLines.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e0e0e0"
              />
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip />
              <Legend />
              {chartLines.map((lineProps, index) => (
                <Line key={index} {...lineProps} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-4 text-muted">
            No channel data available for failure trends.
          </div>
        )}
      </div>

      <div
        className="modal fade"
        id="createBreakerModal"
        tabIndex="-1"
        aria-labelledby="createBreakerModalLabel"
        aria-hidden="true"
        ref={createBreakerModalRef}
      >
        <div className="modal-dialog modal-dialog-centered modal-sm-custom-width">
          <div className="modal-content rounded-4 p-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold" id="createBreakerModalLabel">
                Create New Circuit Breaker
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseCreateBreaker}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body pt-0 pb-4">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Select Channels <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={newBreakerChannel}
                  onChange={(e) => setNewBreakerChannel(e.target.value)}
                  disabled={isSavingNewBreaker}
                >
                  <option value="">Select channel</option>
                  <option value="Email">Email</option>
                  <option value="SMS">SMS</option>
                  <option value="Push">Push</option>
                  <option value="Database">Database</option>
                  <option value="FCM">FCM</option>
                </select>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Failure Threshold <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={newBreakerFailureThreshold}
                    onChange={(e) =>
                      setNewBreakerFailureThreshold(e.target.value)
                    }
                    min="1"
                    disabled={isSavingNewBreaker}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Reset Timeout (seconds){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={newBreakerResetTimeout}
                    onChange={(e) => setNewBreakerResetTimeout(e.target.value)}
                    min="0"
                    disabled={isSavingNewBreaker}
                  />
                </div>
              </div>

              <div className="form-check mt-3 mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="activeCheck"
                  checked={newBreakerIsActive}
                  onChange={(e) => setNewBreakerIsActive(e.target.checked)}
                  disabled={isSavingNewBreaker}
                />
                <label
                  className="form-check-label fw-semibold"
                  htmlFor="activeCheck"
                >
                  Active
                </label>
                <div className="text-muted small">
                  Enable circuit breaking for this channel
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  className="btn btn-outline-dark rounded-3 px-4"
                  onClick={handleCloseCreateBreaker}
                  disabled={isSavingNewBreaker}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-dark rounded-3 px-4"
                  onClick={handleSaveNewBreaker}
                  disabled={isSavingNewBreaker}
                >
                  {isSavingNewBreaker ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span className="ms-2">Saving...</span>
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="editBreakerModal"
        tabIndex="-1"
        aria-labelledby="editBreakerModalLabel"
        aria-hidden="true"
        ref={editBreakerModalRef}
      >
        <div className="modal-dialog modal-dialog-centered modal-sm-custom-width">
          <div className="modal-content rounded-4 p-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title " id="editBreakerModalLabel">
                Edit Circuit Breaker
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseEditBreaker}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body pt-0 pb-4">
              <div className="mb-3">
                <label className="form-label fw-semibold">Channel</label>
                <input
                  type="text"
                  className="form-control"
                  value={editBreakerChannel}
                  disabled
                />
                <div className="text-muted small mt-1">
                  Channel name cannot be changed.
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Failure Threshold <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={editBreakerFailureThreshold}
                    onChange={(e) =>
                      setEditBreakerFailureThreshold(e.target.value)
                    }
                    min="1"
                    disabled={isSavingEditedBreaker}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Reset Timeout (seconds){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={editBreakerResetTimeout}
                    onChange={(e) => setEditBreakerResetTimeout(e.target.value)}
                    min="0"
                    disabled={isSavingEditedBreaker}
                  />
                </div>
              </div>

              <div className="form-check mt-3 mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="editActiveCheck"
                  checked={editBreakerIsActive}
                  onChange={(e) => setEditBreakerIsActive(e.target.checked)}
                  disabled={isSavingEditedBreaker}
                />
                <label
                  className="form-check-label fw-semibold"
                  htmlFor="editActiveCheck"
                >
                  Active
                </label>
                <div className="text-muted small">
                  Enable circuit breaking for this channel
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  className="btn btn-outline-dark rounded-3 px-4"
                  onClick={handleCloseEditBreaker}
                  disabled={isSavingEditedBreaker}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-dark rounded-3 px-4"
                  onClick={handleSaveEditedBreaker}
                  disabled={isSavingEditedBreaker}
                >
                  {isSavingEditedBreaker ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span className="ms-2">Saving...</span>
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="channelHistoryModal"
        tabIndex="-1"
        aria-labelledby="channelHistoryModalLabel"
        aria-hidden="true"
        ref={channelHistoryModalRef}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-4 p-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title" id="channelHistoryModalLabel">
                Channel History -{" "}
                {selectedBreakerForHistory?.displayChannelName || "Loading..."}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseChannelHistory}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body pt-0">
              <div
                className="table-responsive rounded-4"
                style={{ border: "1px solid #dee2e6" }}
              >
                <table className="table align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>
                        <span className="sort-header">
                          Date <i className="bi bi-chevron-expand"></i>
                        </span>
                      </th>
                      <th>
                        <span className="sort-header">
                          Status <i className="bi bi-chevron-expand"></i>
                        </span>
                      </th>
                      <th>
                        <span className="sort-header">
                          Count <i className="bi bi-chevron-expand"></i>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading ? (
                      <tr>
                        <td colSpan="3" className="text-center py-5">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading history...
                            </span>
                          </div>
                          <p className="mt-2 text-muted small">
                            Loading channel history...
                          </p>
                        </td>
                      </tr>
                    ) : historyError ? (
                      <tr>
                        <td colSpan="3" className="text-center py-5">
                          <div className="alert alert-danger mb-0" role="alert">
                            Error loading history: {historyError}
                          </div>
                        </td>
                      </tr>
                    ) : channelHistory.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-3 text-muted">
                          No history data available for this channel.
                        </td>
                      </tr>
                    ) : (
                      channelHistory.map((entry, index) => {
                        const date =
                          entry.created_at || entry.timestamp || "N/A";
                        const status =
                          entry.status ||
                          (entry.is_successful ? "success" : "failed") ||
                          "unknown";
                        const count = entry.count || entry.value || 0;

                        const statusBadgeClass =
                          status.toLowerCase() === "success"
                            ? "status-badge closed"
                            : "status-badge open";
                        const statusText =
                          status.toLowerCase() === "success"
                            ? "Success"
                            : "Failed";

                        return (
                          <tr key={index}>
                            <td className="text-start">
                              {date !== "N/A"
                                ? new Date(date).toLocaleString()
                                : date}
                            </td>
                            <td className="text-start">
                              <span className={statusBadgeClass}>
                                {statusText}
                              </span>
                            </td>
                            <td className="text-start">{count}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="retryConfirmationModal"
        tabIndex="-1"
        aria-labelledby="retryConfirmationModalLabel"
        aria-hidden="true"
        ref={retryConfirmationModalRef}
      >
        <div className="modal-dialog modal-dialog-centered modal-retry-custom-width">
          <div className="modal-content retry-modal rounded-4 p-4">
            <div className="modal-header border-0 pb-0 d-block text-center">
              <h5
                className="modal-title fs-5 mb-3"
                id="retryConfirmationModalLabel"
              >
                Retry Channel Connection{" "}
                {breakerToRetry ? `- ${breakerToRetry.displayChannelName}` : ""}
              </h5>
            </div>
            <div className="modal-body pt-0 text-center">
              <p className="mb-2 text-muted fw-medium fs-6">
                Are you sure you want to retry{" "}
                {breakerToRetry
                  ? `the ${breakerToRetry.displayChannelName}`
                  : "this"}{" "}
                channel now?
              </p>
              <p className="mb-4 text-muted fw-medium fs-6">
                This will attempt to re-activate the channel if it's working
                again and reset its failure count.
              </p>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                  className="btn btn-outline-dark rounded-3 px-4 retry-cancel-btn"
                  onClick={handleCloseRetryConfirmation}
                  disabled={isRetrying}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-dark rounded-3 px-4 retry-confirm-btn"
                  onClick={handleConfirmRetry}
                  disabled={isRetrying}
                >
                  {isRetrying ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span className="ms-2">Retrying...</span>
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitBreakers;
